import React, { forwardRef } from 'react';
import MultiSelection from '@/components/UI/MultiSelection/MultiSelection';

export type TableListEditableMultiSelectionOption = object | string | number;

export type TableListEditableMultiSelectionProps = {
  id: string;
  value: unknown;
  options: TableListEditableMultiSelectionOption[];
  formControlClass: string;
  getAllSelectedData?: (selected: unknown[]) => TableListEditableMultiSelectionOption[];
  getOptionValue?: (option: TableListEditableMultiSelectionOption) => unknown;
  multiSelectionProps?: {
    maxChip?: number;
    withoutTranslations?: boolean;
    isOnlyPlants?: boolean;
    isModelSeries?: boolean;
    isPlants?: boolean;
    size?: 'small' | 'medium';
  };
  onChange: (nextValue: unknown[]) => void;
  onClose?: () => void;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
};

const TableListEditableMultiSelection = forwardRef<HTMLDivElement, TableListEditableMultiSelectionProps>((props, ref) => {
  const {
    id,
    value,
    options,
    formControlClass,
    getAllSelectedData,
    getOptionValue,
    multiSelectionProps,
    onChange,
    onClose,
    onMouseEnter,
  } = props;

  const selectedValues = Array.isArray(value) ? value : [];
  const withoutTranslations = Boolean(multiSelectionProps?.withoutTranslations);
  const isPlants = Boolean(multiSelectionProps?.isPlants);
  const isOnlyPlants = Boolean(multiSelectionProps?.isOnlyPlants);

  const resolveOptionValue = (option: TableListEditableMultiSelectionOption) => {
    if (getOptionValue) return getOptionValue(option);
    if (typeof option === 'object' && option !== null && 'wkzNumber' in option) return option.wkzNumber;

    return option;
  };

  const completeValue = getAllSelectedData
    ? getAllSelectedData(selectedValues)
    : options.filter((option) => selectedValues.includes(resolveOptionValue(option)));

  const handleSelectChange = (event) => {
    if (!Array.isArray(event.target.value)) {
      onChange([]);

      return;
    }

    if (withoutTranslations) {
      onChange(event.target.value);

      return;
    }

    if (isPlants || isOnlyPlants) {
      onChange(event.target.value.map((option) => resolveOptionValue(option)));

      return;
    }

    onChange(event.target.value);
  };

  const handleDeleteChip = (selectedItem) => {
    if (isPlants) {
      const nextValue = completeValue
        .filter((option) => {
          if (typeof option !== 'object' || option === null || !('plantName' in option)) return true;

          return option.plantName !== selectedItem;
        })
        .map((option) => resolveOptionValue(option));

      onChange(nextValue);

      return;
    }

    onChange(selectedValues.filter((currentValue) => currentValue !== selectedItem));
  };

  return (
    <div ref={ref} onMouseEnter={onMouseEnter}>
      <MultiSelection
        id={id}
        label=""
        options={options}
        value={isPlants ? completeValue : selectedValues}
        handleChange={handleSelectChange}
        handleDeleteItem={handleDeleteChip}
        formControlClass={formControlClass}
        variant="standard"
        onClose={onClose}
        disabled={false}
        invalid={false}
        maxChip={multiSelectionProps?.maxChip}
        isOnlyPlants={isOnlyPlants}
        isModelSeries={multiSelectionProps?.isModelSeries}
        isPlants={isPlants}
        withoutTranslations={withoutTranslations}
        size={multiSelectionProps?.size}
        fieldPath={[]}
      />
    </div>
  );
});

TableListEditableMultiSelection.displayName = 'TableListEditableMultiSelection';

export default TableListEditableMultiSelection;
